import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, Image, message } from 'antd';
import { Form, useForm } from 'components/UI/Form';
import { useOpenAccountAuth } from 'hooks/account/useOpenAccountAuth';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import * as Styled from './styles';
import { CartStepEnum, useCartValues } from '../../providers/CartProvider';
import { useAppSelector } from 'appRedux/hooks';
import { getPaymentRedirectUrl } from 'appRedux/restApi';
import { addQuery } from 'utils/generic/query-route';
import { AccountQueryKey } from 'constants/account/account-route-keys';
import {
  EPaymentStatus,
  PaymentMethod
} from 'constants/account/payment-method';
import { IS_FTNFT_AM_MODE, IS_KIOSK_GENERIC } from 'constants/app/configs';
import { ReactQueryKeys } from 'constants/app/react-query-keys';
import CreditCard from 'assets/images/cart-credit-card.png';
import { RestAxiosError } from 'interfaces';

export const CartFooter: FC = () => {
  const { t } = useTranslation();
  const [form] = useForm();
  const { isLoggedIn, isLoginPending } = useIsLoggedIn();
  const { setCartStep, setCartPrice } = useCartValues();
  const { openAccountLoginModal } = useOpenAccountAuth();
  const { data: cartData, info, loading } = useAppSelector(({ cart }) => cart);
  const { data: userData } = useAppSelector(({ user }) => user);

  const {
    refetch,
    isFetching,
    data: paymentRedirectData,
    error
  } = useQuery<
    Awaited<ReturnType<typeof getPaymentRedirectUrl>>,
    RestAxiosError
  >({
    queryKey: [ReactQueryKeys.CART, ReactQueryKeys.CHECKOUT],
    queryFn: () =>
      getPaymentRedirectUrl(undefined, {
        success_url: `${window.location.origin}${window.location.pathname}${
          addQuery({
            [AccountQueryKey.paymentStatus.value]:
              AccountQueryKey.paymentStatus.option[EPaymentStatus.SUCCESS]
          }).search
        }`,
        cancel_url: `${window.location.origin}${window.location.pathname}${
          addQuery({
            [AccountQueryKey.paymentStatus.value]:
              AccountQueryKey.paymentStatus.option[EPaymentStatus.FAILED]
          }).search
        }`
      }),
    enabled: false,
    retry: false
  });

  const checkoutButtonText = useMemo(() => {
    if (isLoggedIn) {
      if (IS_FTNFT_AM_MODE) {
        return (
          <Styled.PaymentButtonTextWrapper>
            <Styled.ButtonText>{t('payment.makePayment')}</Styled.ButtonText>
            <Styled.TextDot>{'\u25CF'}</Styled.TextDot>
            <Styled.ButtonText>{info.formatted_grand_total}</Styled.ButtonText>
          </Styled.PaymentButtonTextWrapper>
        );
      } else {
        return t('cart.checkout');
      }
    }
    if (isLoginPending) {
      return ' ';
    }
    return t('cart.loginToCheckout');
  }, [isLoggedIn, isLoginPending, info.formatted_grand_total]);

  const tableItems = useMemo(() => {
    if (!info.created_at) {
      return [[t('cart.subTotal'), info.formatted_sub_total]];
    }

    return [
      ...(IS_FTNFT_AM_MODE
        ? []
        : [[t('cart.subTotal'), info.formatted_sub_total]]),
      ...(IS_FTNFT_AM_MODE
        ? []
        : [[t('cart.mintPrice'), info.formatted_mint_amount]]),
      ...(!IS_KIOSK_GENERIC ? [] : [[t('cart.vat'), info.formatted_tax_total]]),
      [t('cart.total'), info.formatted_grand_total]
    ];
  }, [info]);

  const handleCheckout = useCallback(() => {
    if (!isLoggedIn) {
      openAccountLoginModal(true);
    } else {
      const cartHasUtilityNft = cartData?.some(item => item.is_utility_nft);

      if (IS_FTNFT_AM_MODE) {
        form
          .validateFields()
          .then(() => {
            if (!cartHasUtilityNft) {
              form.setFieldValue('payment_method', PaymentMethod.CREDIT_CARD);
              form.setFieldValue('email', userData?.email);
              refetch();
            } else {
              setCartPrice(info.formatted_grand_total);
              setCartStep(CartStepEnum.Payment);
            }
          })
          .catch(({ errorFields }) => {
            const firstErrorField = errorFields[0].name[0];
            form.scrollToField(firstErrorField, {
              behavior: 'smooth',
              scrollMode: 'always',
              block: 'center'
            });
          });
      } else {
        setCartPrice(info.formatted_grand_total);
        setCartStep(CartStepEnum.Payment);
      }
    }
  }, [isLoggedIn, info, openAccountLoginModal]);

  useEffect(() => {
    if (paymentRedirectData) {
      const {
        data: { redirect_url }
      } = paymentRedirectData;
      if (!redirect_url) {
        return;
      }
      window.location.href = redirect_url;
    }
  }, [paymentRedirectData]);

  useEffect(() => {
    if (error) {
      const errorMessage = error?.response?.data?.error_code
        ? t(`payment.${error?.response?.data?.error_code}`)
        : error?.response?.data?.message;

      message.error(errorMessage);
    }
  }, [error]);

  if (!info.items_count) {
    return null;
  }

  return (
    <Styled.CartFooterWrapper>
      {IS_FTNFT_AM_MODE ? (
        <Styled.PaymentWrapper>
          <Form form={form}>
            <Styled.CardDetailWrapper>
              <Styled.CardWrapper>
                <Image src={CreditCard} preview={false} />
                <Styled.CardText>{t('payment.creditCard')}</Styled.CardText>
              </Styled.CardWrapper>
              <Styled.CardSubText>{`\u25CF ${t(
                'payment.availablePaymentOptions'
              )}`}</Styled.CardSubText>
            </Styled.CardDetailWrapper>
            <Styled.FormItemWide
              name="payment_agreement"
              initialValue={false}
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(
                        t('account.requiredReadAgreeTermsAndConditions')
                      );
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Checkbox>
                <Styled.Label>{t('account.paymentAgreement')}</Styled.Label>
              </Checkbox>
            </Styled.FormItemWide>
          </Form>
        </Styled.PaymentWrapper>
      ) : (
        <Styled.AppTable tableItems={tableItems} $loading={loading} />
      )}
      {!info.created_at && (
        <Styled.AuthHint>{t('cart.authHint')}</Styled.AuthHint>
      )}
      <Styled.Button
        type="primary"
        disabled={loading || isLoginPending || isFetching}
        loading={isLoginPending || isFetching}
        onClick={handleCheckout}
      >
        {checkoutButtonText}
      </Styled.Button>
    </Styled.CartFooterWrapper>
  );
};
